import React, { useState, useEffect } from 'react';
import { theme } from '../utils/theme';
import {
  subscribeCRMRecords,
  saveCRMRecord,
  deleteCRMRecord,
  subscribeCRMInteractions,
  saveCRMInteraction,
  computeScheduleSummaryFromInteractions
} from '../firebase/database';
import { useAuth } from '../context/AuthContext';

const C = theme.colors;

function toJSDate(val) {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (typeof val.seconds === 'number') return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function formatCRMDate(val) {
  const d = toJSDate(val);
  return d ? d.toLocaleDateString('en-IN') : '-';
}

function toInputDateStr(val) {
  if (!val) return '';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const d = toJSDate(val);
  return d ? d.toISOString().split('T')[0] : '';
}

export default function CRM() {
  const { studentId, isStudent } = useAuth();
  const scopedStudentId = isStudent ? studentId : null;

  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

  // Scoped Drawer / Modal state for Prospect Interactions
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [submittingInteraction, setSubmittingInteraction] = useState(false);

  const todayISO = new Date().toISOString().split('T')[0];
  const [interactionForm, setInteractionForm] = useState({
    id: null,
    type: 'Meeting',
    date: todayISO,
    duration: '',
    subject: '',
    next_schedule_date: ''
  });

  // Form State for Adding New Lead (Schedule dates removed; derived dynamically from interactions)
  const [newRecord, setNewRecord] = useState({
    name: '',
    fatherName: '',
    caste: '',
    casteCategory: 'GEN',
    address: '',
    age: '',
    mobileNumber: '',
    ref: '',
    businessExpected: ''
  });

  // Subscribe to CRM records from Firestore scoped to student or global for others
  useEffect(() => {
    const unsub = subscribeCRMRecords(scopedStudentId, setRecords);
    return unsub;
  }, [scopedStudentId]);

  // Subscribe to interactions subcollection when a prospect is opened in the drawer
  useEffect(() => {
    if (!selectedProspect?.id) {
      setInteractions([]);
      return;
    }
    setLoadingInteractions(true);
    const prospectId = selectedProspect.id;
    const unsub = subscribeCRMInteractions(scopedStudentId, prospectId, (list) => {
      setInteractions(list);
      setLoadingInteractions(false);

      // Dynamically derive 'L' and 'N' from the live interactions list and sync to directory table
      const summary = computeScheduleSummaryFromInteractions(list);
      setRecords(prev =>
        prev.map(rec => (rec.id === prospectId ? { ...rec, ...summary } : rec))
      );
      setSelectedProspect(prev =>
        prev && prev.id === prospectId ? { ...prev, ...summary } : prev
      );
    });
    return unsub;
  }, [scopedStudentId, selectedProspect?.id]);

  const resetInteractionForm = () => {
    setInteractionForm({
      id: null,
      type: 'Meeting',
      date: new Date().toISOString().split('T')[0],
      duration: '',
      subject: '',
      next_schedule_date: ''
    });
  };

  const handleOpenInteractions = (prospect) => {
    setSelectedProspect(prospect);
    resetInteractionForm();
  };

  const handleEditInteractionClick = (item) => {
    setInteractionForm({
      id: item.id,
      type: item.type === 'Call' ? 'Call' : 'Meeting',
      date: toInputDateStr(item.date) || new Date().toISOString().split('T')[0],
      duration: item.duration !== undefined && item.duration !== null ? String(item.duration) : '',
      subject: item.subject || '',
      next_schedule_date: toInputDateStr(item.next_schedule_date),
      created_at: item.created_at || null
    });
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    if (!selectedProspect?.id) return;
    if (!interactionForm.date) {
      alert('Interaction Date is required!');
      return;
    }
    if (!interactionForm.subject.trim()) {
      alert('Subject / Discussion notes are required!');
      return;
    }

    setSubmittingInteraction(true);
    try {
      const { interaction, parentUpdate } = await saveCRMInteraction(
        scopedStudentId,
        selectedProspect.id,
        interactionForm
      );

      // Immediately update local UI state for interactions, table records ('L' & 'N'), and drawer header
      setInteractions(prev => {
        const filtered = prev.filter(item => item.id !== interaction.id);
        const updated = [interaction, ...filtered];
        return updated.sort((a, b) => {
          const timeA = toJSDate(a.date)?.getTime() || 0;
          const timeB = toJSDate(b.date)?.getTime() || 0;
          return timeB - timeA;
        });
      });

      setRecords(prev =>
        prev.map(rec => (rec.id === selectedProspect.id ? { ...rec, ...parentUpdate } : rec))
      );

      setSelectedProspect(prev => (prev ? { ...prev, ...parentUpdate } : null));

      resetInteractionForm();
    } catch (err) {
      console.error('Failed to save interaction:', err);
      alert('Error saving interaction: ' + err.message);
    } finally {
      setSubmittingInteraction(false);
    }
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    if (!newRecord.name) {
      alert("Name is a required field!");
      return;
    }
    
    // Construct the payload (schedule dates default to null/empty on creation)
    const payload = {
      ...newRecord,
      age: newRecord.age ? Number(newRecord.age) : '',
      businessExpected: newRecord.businessExpected ? Number(newRecord.businessExpected) : 0,
      last_meeting_date: null,
      next_meeting_date: null,
      lastMeetingDate: '',
      nextMeetingDate: ''
    };

    await saveCRMRecord(scopedStudentId, payload);

    // Reset the form
    setNewRecord({
      name: '',
      fatherName: '',
      caste: '',
      casteCategory: 'GEN',
      address: '',
      age: '',
      mobileNumber: '',
      ref: '',
      businessExpected: ''
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingRecord.name) {
      alert("Name is a required field!");
      return;
    }

    const payload = {
      ...editingRecord,
      age: editingRecord.age ? Number(editingRecord.age) : '',
      businessExpected: editingRecord.businessExpected ? Number(editingRecord.businessExpected) : 0
    };

    await saveCRMRecord(isStudent ? studentId : null, payload);
    setEditingRecord(null);
  };

  const handleDeleteRecord = async (id) => {
    if (window.confirm("Are you sure you want to delete this CRM record?")) {
      await deleteCRMRecord(isStudent ? studentId : null, id);
    }
  };

  const handlePrint = () => {
    const tableWrapper = document.querySelector('.crm-table-wrapper');
    if (!tableWrapper) {
      alert("Table not found!");
      return;
    }
    const printWindow = window.open('', '_blank');
    const clonedWrapper = tableWrapper.cloneNode(true);
    
    // Remove search box
    const searchBox = clonedWrapper.querySelector('input[placeholder*="Search"]');
    if (searchBox) searchBox.parentElement.remove();
    
    // Remove actions column header and cells from the cloned table
    const headerCells = Array.from(clonedWrapper.querySelectorAll('th'));
    const actionsIndex = headerCells.findIndex(th => th.textContent.toLowerCase().includes('actions'));
    
    if (actionsIndex !== -1) {
      const rows = clonedWrapper.querySelectorAll('tr');
      rows.forEach(tr => {
        const cells = tr.querySelectorAll('td, th');
        if (cells[actionsIndex]) {
          cells[actionsIndex].remove();
        }
      });
    }
    
    const footerRows = clonedWrapper.querySelectorAll('tfoot tr');
    footerRows.forEach(tr => {
      // If the last cell is empty and spans nothing, remove it to fix alignment
      const cells = tr.querySelectorAll('td');
      if (cells.length > 0) {
        const lastCell = cells[cells.length - 1];
        if (lastCell.textContent.trim() === '' && lastCell.colSpan === 1) {
          lastCell.remove();
        }
      }
    });
    
    printWindow.document.write(`
      <html>
        <head>
          <title>CRM Prospects</title>
          <style>
            @page { 
              size: landscape; 
              margin: 1.5cm 1cm 1.5cm 1cm;
              @bottom-left {
                content: "Dev. of Pankaj Comp.";
                font-size: 10px;
                color: #666;
              }
              @bottom-right {
                content: "Page " counter(page);
                font-size: 10px;
                color: #666;
              }
            }
            table { width: 100%; border-collapse: collapse; font-family: sans-serif; }
            th, td { padding: 6px; border: 1px solid #ddd; text-align: left; overflow: hidden; word-wrap: break-word; }
            th { background-color: #f2f2f2; text-transform: uppercase; font-size: 9px; }
            td { font-size: 11px; }
            tr { break-inside: avoid; }
            h1 { text-align: center; font-size: 18px; margin: 0 0 5px 0; }
            h2 { text-align: center; font-size: 14px; margin: 0 0 10px 0; }
            /* Hide the original tfoot */
            tfoot { display: none; }
          </style>
        </head>
        <body>
          <h1>Pt. Deen Dayal Upadhyay Medical and Research Centre, Anupgarh</h1>
          <h2>CRM Prospects Directory</h2>
          ${clonedWrapper.innerHTML}
          ${clonedWrapper.querySelector('tfoot') ? `
            <table style="width: 100%; border-collapse: collapse; font-family: sans-serif; margin-top: 10px;">
              ${clonedWrapper.querySelector('tfoot').innerHTML}
            </table>
          ` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Filter & Search Records
  const filteredRecords = records.filter(r => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (r.name || '').toLowerCase().includes(searchLower) ||
      (r.fatherName || '').toLowerCase().includes(searchLower) ||
      (r.caste || '').toLowerCase().includes(searchLower) ||
      (r.casteCategory || '').toLowerCase().includes(searchLower) ||
      (r.address || '').toLowerCase().includes(searchLower) ||
      (r.mobileNumber || '').toLowerCase().includes(searchLower) ||
      (r.ref || '').toLowerCase().includes(searchLower)
    );
  });

  // Sort Filtered Records
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    let valA = sortConfig.key === 'lastMeetingDate' ? (a.last_meeting_date || a.lastMeetingDate) : a[sortConfig.key];
    let valB = sortConfig.key === 'lastMeetingDate' ? (b.last_meeting_date || b.lastMeetingDate) : b[sortConfig.key];

    if (sortConfig.key === 'lastMeetingDate') {
      valA = toJSDate(valA)?.getTime() ?? null;
      valB = toJSDate(valB)?.getTime() ?? null;
    }

    // Handle string comparisons gracefully
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    // Default empty values to the bottom
    if (valA === undefined || valA === null || valA === '') return 1;
    if (valB === undefined || valB === null || valB === '') return -1;

    if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
    return 0;
  });

  // Summary statistics
  const totalExpectedBusiness = filteredRecords.reduce((sum, r) => sum + (Number(r.businessExpected) || 0), 0);
  const totalLeads = filteredRecords.length;
  const scStObcCount = filteredRecords.filter(r => ['SC', 'ST', 'OBC'].includes(r.casteCategory)).length;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', fontFamily: theme.fonts.sans, color: C.text }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 5 }}>CRM Portal</h1>
      <button 
          onClick={handlePrint}
          style={{ padding: '10px 20px', borderRadius: 8, background: 'white', border: `1px solid ${C.border}`, cursor: 'pointer', fontWeight: 600 }}
        >
          Print Table
        </button>
      <p style={{ color: C.muted, marginBottom: 25, fontSize: 14 }} className="no-print">Manage client relationship, tracking calls, meetings, and expected business opportunities.</p>

      {/* Analytics widgets */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, marginBottom: 25 }} className="no-print">
        <div style={{ background: C.card, padding: '20px', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: theme.shadows.soft }}>
          <div style={{ fontSize: 13, color: C.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 5 }}>Total Active Leads</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.accent }}>{totalLeads}</div>
        </div>
        <div style={{ background: C.card, padding: '20px', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: theme.shadows.soft }}>
          <div style={{ fontSize: 13, color: C.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 5 }}>Expected Business</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.green }}>₹{totalExpectedBusiness.toLocaleString('en-IN')}</div>
        </div>
        <div style={{ background: C.card, padding: '20px', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: theme.shadows.soft }}>
          <div style={{ fontSize: 13, color: C.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 5 }}>Caste Reservation Leads</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.purple }}>{scStObcCount} <span style={{ fontSize: 13, fontWeight: 400, color: C.muted }}>SC/ST/OBC</span></div>
        </div>
      </div>

      {/* Add New CRM Record Form */}
      <div style={{ background: C.card, padding: 25, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 30, boxShadow: theme.shadows.soft }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 15, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Add CRM Record / Prospect</h3>
        <form onSubmit={handleAddRecord}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 15, marginBottom: 15 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Full Name *</label>
              <input 
                type="text" 
                placeholder="Name" 
                value={newRecord.name} 
                onChange={e => setNewRecord({...newRecord, name: e.target.value})} 
                required
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Father's Name</label>
              <input 
                type="text" 
                placeholder="Father's Name" 
                value={newRecord.fatherName} 
                onChange={e => setNewRecord({...newRecord, fatherName: e.target.value})} 
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Caste</label>
              <input 
                type="text" 
                placeholder="e.g. Rajput, Yadav..." 
                value={newRecord.caste} 
                onChange={e => setNewRecord({...newRecord, caste: e.target.value})} 
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Caste Category</label>
              <select 
                value={newRecord.casteCategory} 
                onChange={e => setNewRecord({...newRecord, casteCategory: e.target.value})} 
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, background: 'white' }}
              >
                <option value="GEN">GEN (General)</option>
                <option value="OBC">OBC (Other Backward Class)</option>
                <option value="SC">SC (Scheduled Caste)</option>
                <option value="ST">ST (Scheduled Tribe)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Age</label>
              <input 
                type="number" 
                placeholder="Age" 
                value={newRecord.age} 
                onChange={e => setNewRecord({...newRecord, age: e.target.value})} 
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Mobile Number</label>
              <input 
                type="tel" 
                placeholder="Mobile Number" 
                value={newRecord.mobileNumber} 
                onChange={e => setNewRecord({...newRecord, mobileNumber: e.target.value})} 
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Expected Business (in Rs.)</label>
              <input 
                type="number" 
                placeholder="Expected Revenue (Rs.)" 
                value={newRecord.businessExpected} 
                onChange={e => setNewRecord({...newRecord, businessExpected: e.target.value})} 
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 15 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Address</label>
              <input 
                type="text" 
                placeholder="Full Address" 
                value={newRecord.address} 
                onChange={e => setNewRecord({...newRecord, address: e.target.value})} 
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Ref (Notes / Referrals)</label>
              <textarea 
                placeholder="Reference info, requirements, call discussion details..." 
                value={newRecord.ref} 
                onChange={e => setNewRecord({...newRecord, ref: e.target.value})} 
                rows="1"
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button 
              type="submit" 
              style={{ padding: '12px 25px', borderRadius: 8, background: C.accent, color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', boxShadow: theme.shadows.accent }}
            >
              Add CRM Record
            </button>
          </div>
        </form>
      </div>

      {/* CRM Records Table Card */}
      <div style={{ background: C.card, padding: 25, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: theme.shadows.soft }} className="crm-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>CRM Prospects Directory</h2>
          <input 
            placeholder="Search leads by Name, Caste, Category, Mob..." 
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)} 
            style={{ padding: '10px 15px', borderRadius: 8, border: `1px solid ${C.border}`, width: '100%', maxWidth: '350px', fontSize: 14 }}
          />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: C.bg, borderBottom: `2px solid ${C.border}` }}>
                <th style={{ padding: '12px', fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', width: 50 }}>S.No</th>
                {[
                  { key: 'name', label: 'Prospect Details' },
                  { key: 'caste', label: 'Caste & Cat.' },
                  { key: 'age', label: 'Age' },
                  { key: 'mobileNumber', label: 'Mobile' },
                  { key: 'lastMeetingDate', label: 'Call Schedule' },
                  { key: 'businessExpected', label: 'Expected (Rs.)' },
                  { key: 'ref', label: 'Ref/Notes' }
                ].map(col => (
                  <th 
                    key={col.key} 
                    onClick={() => requestSort(col.key)} 
                    style={{ padding: '12px', fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {col.label} {sortConfig.key === col.key ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                  </th>
                ))}
                <th style={{ padding: '12px', fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: C.muted }}>
                    No CRM records found. Add a record above to get started.
                  </td>
                </tr>
              ) : (
                sortedRecords.map((r, index) => (
                  <tr key={r.id} style={{ 
                    borderBottom: `1px solid ${C.border}`, 
                    verticalAlign: 'top', 
                    background: (r.ref || '').toUpperCase().includes('RISK') ? C.redSoft : 'transparent' 
                  }}>
                    <td style={{ padding: '12px', fontSize: 14 }}>{index + 1}</td>
                    <td style={{ padding: '12px', fontSize: 14 }}>
                      <div style={{ fontWeight: 700, color: C.text }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: C.muted }}>{r.fatherName || '-'}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{r.address || '-'}</div>
                    </td>
                    <td style={{ padding: '12px', fontSize: 14 }}>
                      <div>{r.caste || '-'}</div>
                      <span style={{ 
                        display: 'inline-block', 
                        marginTop: 4,
                        padding: '2px 6px', 
                        borderRadius: 4, 
                        fontSize: 10, 
                        fontWeight: 700,
                        background: r.casteCategory === 'GEN' ? C.dim : r.casteCategory === 'OBC' ? C.accentSoft : C.purpleSoft,
                        color: r.casteCategory === 'GEN' ? C.text : r.casteCategory === 'OBC' ? C.accent : C.purple
                      }}>
                        {r.casteCategory}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: 14 }}>{r.age || '-'}</td>
                    <td style={{ padding: '12px', fontSize: 14 }}>{r.mobileNumber || '-'}</td>
                    <td style={{ padding: '12px', fontSize: 13 }}>
                      <div style={{ fontSize: 11, color: C.muted }}>
                        L: {formatCRMDate(r.last_meeting_date || r.lastMeetingDate)}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: (r.next_meeting_date || r.nextMeetingDate) ? C.amber : C.muted }}>
                        N: {formatCRMDate(r.next_meeting_date || r.nextMeetingDate)}
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: 14, fontWeight: 600, color: C.green }}>
                      ₹{(Number(r.businessExpected) || 0).toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding: '12px', fontSize: 13, maxWidth: '200px', whiteSpace: 'pre-wrap', color: C.muted }}>
                      {r.ref || '-'}
                    </td>
                    <td style={{ padding: '12px', fontSize: 14, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => handleOpenInteractions(r)}
                        style={{
                          marginRight: 8,
                          padding: '5px 10px',
                          borderRadius: 6,
                          border: `1px solid ${C.accent}`,
                          background: C.accentSoft,
                          color: C.accent,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        Show
                      </button>
                      <button 
                        onClick={() => setEditingRecord({ ...r })} 
                        style={{ marginRight: 8, padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteRecord(r.id)} 
                        style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.red}`, background: C.redSoft, color: C.red, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Del
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {sortedRecords.length > 0 && (
              <tfoot style={{ background: C.bg, fontWeight: 700 }}>
                <tr>
                  <td style={{ padding: '15px' }} colSpan="3">Filtered Total ({totalLeads} Records)</td>
                  <td colSpan="3"></td>
                  <td style={{ padding: '15px', color: C.green, fontSize: 15 }} colSpan="2">
                    ₹{totalExpectedBusiness.toLocaleString('en-IN')}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Editing Dialog Modal-like Overlay */}
      {editingRecord && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: C.card,
            padding: 30,
            borderRadius: 12,
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            border: `1px solid ${C.border}`
          }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Edit CRM Record</h3>
            <form onSubmit={handleSaveEdit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 15, marginBottom: 15 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Full Name *</label>
                  <input 
                    type="text" 
                    value={editingRecord.name} 
                    onChange={e => setEditingRecord({...editingRecord, name: e.target.value})} 
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Father's Name</label>
                  <input 
                    type="text" 
                    value={editingRecord.fatherName || ''} 
                    onChange={e => setEditingRecord({...editingRecord, fatherName: e.target.value})} 
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Caste</label>
                  <input 
                    type="text" 
                    value={editingRecord.caste || ''} 
                    onChange={e => setEditingRecord({...editingRecord, caste: e.target.value})} 
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Caste Category</label>
                  <select 
                    value={editingRecord.casteCategory || 'GEN'} 
                    onChange={e => setEditingRecord({...editingRecord, casteCategory: e.target.value})} 
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, background: 'white' }}
                  >
                    <option value="GEN">GEN (General)</option>
                    <option value="OBC">OBC (Other Backward Class)</option>
                    <option value="SC">SC (Scheduled Caste)</option>
                    <option value="ST">ST (Scheduled Tribe)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Age</label>
                  <input 
                    type="number" 
                    value={editingRecord.age || ''} 
                    onChange={e => setEditingRecord({...editingRecord, age: e.target.value})} 
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Mobile Number</label>
                  <input 
                    type="tel" 
                    value={editingRecord.mobileNumber || ''} 
                    onChange={e => setEditingRecord({...editingRecord, mobileNumber: e.target.value})} 
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Expected Business (in Rs.)</label>
                  <input 
                    type="number" 
                    value={editingRecord.businessExpected || ''} 
                    onChange={e => setEditingRecord({...editingRecord, businessExpected: e.target.value})} 
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Address</label>
                  <input 
                    type="text" 
                    value={editingRecord.address || ''} 
                    onChange={e => setEditingRecord({...editingRecord, address: e.target.value})} 
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: C.muted }}>Ref (Notes / Referrals)</label>
                  <textarea 
                    value={editingRecord.ref || ''} 
                    onChange={e => setEditingRecord({...editingRecord, ref: e.target.value})} 
                    rows="2"
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button 
                  type="button" 
                  onClick={() => setEditingRecord(null)}
                  style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'white', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={{ padding: '10px 20px', borderRadius: 8, background: C.green, color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer' }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scoped Prospect Interactions Side-Drawer */}
      {selectedProspect && (
        <div
          onClick={() => setSelectedProspect(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(17, 24, 39, 0.45)',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 1100,
            backdropFilter: 'blur(3px)'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.card,
              width: '100%',
              maxWidth: '540px',
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-8px 0 30px rgba(0,0,0,0.15)',
              borderLeft: `1px solid ${C.border}`
            }}
          >
            {/* Drawer Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${C.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              background: C.bg
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Prospect Interactions
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: C.text }}>
                  {selectedProspect.name}
                </h3>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
                  {selectedProspect.mobileNumber ? `📱 ${selectedProspect.mobileNumber}` : 'No mobile'}
                  {selectedProspect.address ? ` • 📍 ${selectedProspect.address}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12 }}>
                  <span>
                    <strong style={{ color: C.muted }}>Last Interaction:</strong>{' '}
                    <span style={{ fontWeight: 700, color: C.text }}>
                      {formatCRMDate(selectedProspect.last_meeting_date || selectedProspect.lastMeetingDate)}
                    </span>
                  </span>
                  <span>
                    <strong style={{ color: C.muted }}>Next Follow-up:</strong>{' '}
                    <span style={{ fontWeight: 700, color: C.amber }}>
                      {formatCRMDate(selectedProspect.next_meeting_date || selectedProspect.nextMeetingDate)}
                    </span>
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedProspect(null)}
                style={{
                  background: 'white',
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  color: C.muted
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Scrollable Drawer Body */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Add / Edit Interaction Form */}
              <div style={{
                background: C.bg,
                padding: '18px',
                borderRadius: 10,
                border: `1px solid ${interactionForm.id ? C.accent : C.border}`,
                marginBottom: 24
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: interactionForm.id ? C.accent : C.text }}>
                    {interactionForm.id ? '✎ Update Interaction' : '+ Log New Interaction'}
                  </h4>
                  {interactionForm.id && (
                    <button
                      type="button"
                      onClick={resetInteractionForm}
                      style={{
                        background: 'white',
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        padding: '3px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: C.muted
                      }}
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
                <form onSubmit={handleAddInteraction}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>
                        Type *
                      </label>
                      <select
                        value={interactionForm.type}
                        onChange={e => setInteractionForm({ ...interactionForm, type: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '9px 10px',
                          borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          fontSize: 13,
                          background: 'white'
                        }}
                      >
                        <option value="Meeting">Meeting</option>
                        <option value="Call">Call</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>
                        Interaction Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={interactionForm.date}
                        onChange={e => setInteractionForm({ ...interactionForm, date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          fontSize: 13,
                          background: 'white'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>
                        Duration (Minutes)
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="e.g. 30"
                        value={interactionForm.duration}
                        onChange={e => setInteractionForm({ ...interactionForm, duration: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          fontSize: 13,
                          background: 'white'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>
                        Next Schedule Date
                      </label>
                      <input
                        type="date"
                        value={interactionForm.next_schedule_date}
                        onChange={e => setInteractionForm({ ...interactionForm, next_schedule_date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          fontSize: 13,
                          background: 'white'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>
                      Subject / Discussion Notes *
                    </label>
                    <textarea
                      required
                      rows="3"
                      placeholder="Key discussion points, client requirements, outcome..."
                      value={interactionForm.subject}
                      onChange={e => setInteractionForm({ ...interactionForm, subject: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '9px 10px',
                        borderRadius: 8,
                        border: `1px solid ${C.border}`,
                        fontSize: 13,
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        background: 'white'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={submittingInteraction}
                      style={{
                        padding: '9px 18px',
                        borderRadius: 8,
                        background: C.accent,
                        color: 'white',
                        border: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: submittingInteraction ? 'not-allowed' : 'pointer',
                        opacity: submittingInteraction ? 0.7 : 1,
                        boxShadow: theme.shadows.accent
                      }}
                    >
                      {submittingInteraction
                        ? 'Saving...'
                        : interactionForm.id
                          ? 'Update Interaction'
                          : 'Save Interaction'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Interaction History List */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                    Interaction History ({interactions.length})
                  </h4>
                  <span style={{ fontSize: 11, color: C.muted }}>Ordered by Date (Newest First)</span>
                </div>

                {loadingInteractions ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: C.muted, fontSize: 13 }}>
                    Loading interactions...
                  </div>
                ) : interactions.length === 0 ? (
                  <div style={{
                    padding: '30px 20px',
                    textAlign: 'center',
                    color: C.muted,
                    fontSize: 13,
                    border: `1px dashed ${C.border}`,
                    borderRadius: 10
                  }}>
                    No interactions logged for this prospect yet. Use the form above to record a meeting or call.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {interactions.map(item => {
                      const isMeeting = item.type === 'Meeting';
                      return (
                        <div
                          key={item.id}
                          style={{
                            padding: '14px 16px',
                            borderRadius: 10,
                            border: `1px solid ${interactionForm.id === item.id ? C.accent : C.border}`,
                            background: 'white',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '3px 8px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 700,
                                background: isMeeting ? C.purpleSoft : C.greenSoft,
                                color: isMeeting ? C.purple : C.green
                              }}>
                                {isMeeting ? '🤝 Meeting' : '📞 Call'}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                {formatCRMDate(item.date)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {item.duration ? (
                                <span style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: C.muted,
                                  background: C.bg,
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  border: `1px solid ${C.border}`
                                }}>
                                  ⏱ {item.duration} {typeof item.duration === 'number' || /^\d+$/.test(String(item.duration)) ? 'mins' : ''}
                                </span>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => handleEditInteractionClick(item)}
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  border: `1px solid ${C.border}`,
                                  background: 'white',
                                  cursor: 'pointer',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: C.text
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          </div>

                          <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {item.subject || '-'}
                          </div>

                          {item.next_schedule_date && (
                            <div style={{
                              marginTop: 10,
                              paddingTop: 8,
                              borderTop: `1px dashed ${C.border}`,
                              fontSize: 12,
                              color: C.amber,
                              fontWeight: 600
                            }}>
                              📅 Next Follow-up Scheduled: {formatCRMDate(item.next_schedule_date)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
